'use client';

import { addDDError } from '@wwe-portal/web/libs/analytics/datadog';
import { tryGetDeviceToken } from '@wwe-portal/web/libs/analytics/titan';
import {
  LOGIN_METHODS,
  useAuthStore,
} from '@wwe-portal/web/providers/stores/auth-store';
import { FB_APP_ID } from '@wwe-portal/web/utils/config';
import { FacebookButton as FacebookButtonUI } from '@wwe-portal/ui/features/auth';
import { toast } from '@wwe-portal/ui/components/toast';
import {
  FacebookLoginClient,
  LoginResponse,
} from '@greatsumini/react-facebook-login';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';

export const FacebookLogin = ({ className }: { className?: string }) => {
  const { isAuthenticating, loginMethod, loginWithFacebook } = useAuthStore();
  const t = useTranslations();

  const responseFacebook = async (response: LoginResponse) => {
    const accessToken = response.authResponse?.accessToken;
    const deviceTokenDiagnostics: Record<string, unknown> = {};
    const deviceToken = await tryGetDeviceToken(deviceTokenDiagnostics);

    // Without both tokens there is nothing to send to /auth/v2/fb. Bailing out
    // silently here leaves the login dialog open with no feedback and no RUM
    // trace, so report it the same way the Scopely callback does. `status`
    // separates a Facebook-side failure (not_authorized/unknown) from a device
    // token the Titan tracker never produced (commonly a blocked tracker).
    if (!accessToken || !deviceToken) {
      toast({
        variant: 'error',
        content: t('login_process_is_canceled'),
      });
      addDDError(new Error('Invalid loginWithFacebook params'), {
        status: response.status,
        // Presence only — the raw values are credentials and must not reach RUM.
        hasAccessToken: !!accessToken,
        hasDeviceToken: !!deviceToken,
        ...deviceTokenDiagnostics,
      });
      return;
    }

    // Login here
    loginWithFacebook({ accessToken, deviceToken });
  };
  const handleLogin = () => {
    FacebookLoginClient.login((response) => responseFacebook(response), {
      scope: 'public_profile, email',
    });
  };
  if (!FB_APP_ID) throw new Error('FB_APP_ID is missing');
  useEffect(() => {
    let unmounted = false;
    const initFB = async () => {
      if (unmounted) return;
      FacebookLoginClient.clear();
      await FacebookLoginClient.loadSdk('en_US');
      if (!FB_APP_ID) throw new Error('FB_APP_ID is missing');
      FacebookLoginClient.init({
        appId: FB_APP_ID,
        version: 'v17.0' as never,
      });
    };
    initFB();
    return () => {
      unmounted = true;
    };
  }, []);
  if (isAuthenticating && loginMethod === LOGIN_METHODS.WITH_FACEBOOK)
    return <FacebookButtonUI loading loadingText="Processing..." />;
  return <FacebookButtonUI onClick={handleLogin} className={className} />;
};
